import { configureStore } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';

import { apiSlice } from './slices/apiSlice';
import { deviceReportingSlice } from './slices/deviceReportingApiSlice';
import loggedUserSlice from './slices/loggedUserSlice';
import { mdmManagementSlice } from './slices/mdmManagementApiSlice';
import { signupApiSlice } from './slices/signupApiSlice';
import { gigawattBackendApiSlice } from './slices/gigawattBackendApiSlice';
import temporalWorkflowsSlice from './slices/temporalWorkflowsSlice';
import copilotKitChatSlice from './slices/copilotKitChatSlice';
import messageFeedbackSlice from './slices/messageFeedbackSlice';
import aiChatDrawerSlice from './slices/aiChatDrawerSlice';
import { googleApiSlice } from './slices/googleApiSlice';
import groupChangeSubResourcesReducer from './slices/groupChangeSubResourcesSlice';
import storefrontContextSlice from './slices/storefrontContextSlice';

// Create the store and automatically add "thunk middleware" and set Redux DevTools
export const store = configureStore({
  reducer: {
    [apiSlice.reducerPath]: apiSlice.reducer,
    [loggedUserSlice.name]: loggedUserSlice.reducer,
    [deviceReportingSlice.reducerPath]: deviceReportingSlice.reducer,
    [mdmManagementSlice.reducerPath]: mdmManagementSlice.reducer,
    [googleApiSlice.reducerPath]: googleApiSlice.reducer,
    [signupApiSlice.reducerPath]: signupApiSlice.reducer,
    [gigawattBackendApiSlice.reducerPath]: gigawattBackendApiSlice.reducer,
    [temporalWorkflowsSlice.name]: temporalWorkflowsSlice.reducer,
    copilotKitChat: copilotKitChatSlice.reducer,
    [messageFeedbackSlice.name]: messageFeedbackSlice.reducer,
    [aiChatDrawerSlice.name]: aiChatDrawerSlice.reducer,
    groupChangeSubResources: groupChangeSubResourcesReducer,
    [storefrontContextSlice.name]: storefrontContextSlice.reducer,
  },
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware().concat(
      googleApiSlice.middleware,
      apiSlice.middleware,
      deviceReportingSlice.middleware,
      mdmManagementSlice.middleware,
      signupApiSlice.middleware,
      gigawattBackendApiSlice.middleware
    ),
});

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
type AppDispatch = typeof store.dispatch;

// Use throughout the app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
